import { useState, useEffect } from "react";
import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import "survey-core/defaultV2.css";
import "survey-creator-core/survey-creator-core.css";
import { Autocomplete, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { __API_URL__ } from "../../SERVER_URL";
import NavBar from "../../components/navBar/navBar";
import Loader from "../../components/Loader";

// label = display name (matches RolesGetSerializer.get_name), value = Role.title sent to backend
const SURVEY_ROLES = [
  { label: "CRO Admin", value: "CRO Admin" },
  { label: "CRO Coordinator", value: "Cro_coordinator" },
  { label: "Data Manager", value: "Data Manager" },
  { label: "INV", value: "Investigator" },
  { label: "CRC", value: "Inv_coordinator" },
  { label: "Sub Investigator", value: "Sub_investigator" },
  { label: "RVR", value: "Reviewer" },
];

export default function SurveyCreatorWidget() {
  const [creator, setCreator] = useState(null);
  const [surveyJson, setSurveyJson] = useState(null);
  const [currentPage, setCurrentPage] = useState({ trial_type: [] });
  const [name, setName] = useState("");
  const [allTrialType, setAllTrialType] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const surveyId = queryParameters.get("surveyId");

  // Initialize Survey Creator
  useEffect(() => {
    if (!creator) {
      const options = {
        showLogicTab: true,
        showTranslationTab: true,
        isAutoSave: false,
      };

      const creatorInstance = new SurveyCreator(options);
      creatorInstance.showState = true;
      setCreator(creatorInstance);
    }
  }, [creator]);

  // Load survey data
  useEffect(() => {
    if (creator && surveyJson) {
      creator.JSON = surveyJson;
    }
  }, [creator, surveyJson]);

  const handleSave = () => {
    if (!name?.trim()) {
      toast.error("Survey name is required");
      return;
    }
    if (!surveyId) {
      saveSurvey(creator.JSON);
    } else {
      updateSurvey(surveyId, creator.JSON);
    }
  };

  const saveSurvey = (surveyObject) => {
    const trialTypeIds = (currentPage.trial_type || []).map((obj) => obj.value);
    axios
      .post(
        `${__API_URL__}/api/ecrf/create_new_ecrf/`,
        {
          name: name,
          surveyJson: surveyObject,
          trial_type_ids: trialTypeIds,
          role_titles: selectedRoles.map((r) => r.value),
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success("Source Document Saved");
        navigate(`/form-creater-v2/?surveyId=${res.data.data.id}`);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      });
  };

  const updateSurvey = (surveyId, surveyObject) => {
    const trialTypeIds = (currentPage.trial_type || []).map((obj) => obj.value);
    axios
      .patch(
        `${__API_URL__}/api/ecrf/update_new_ecrf/?surveyId=${surveyId}`,
        {
          name: name,
          surveyJson: surveyObject,
          trial_type_ids: trialTypeIds,
          role_titles: selectedRoles.map((r) => r.value),
        },
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      )
      .then((res) => {
        toast.success("Source Document Updated");
      })
      .catch((err) => {
        toast.error("Something Wrong!");
      });
  };

  const getSurvey = () => {
    let id = surveyId || "";
    setIsLoading(true);
    axios
      .get(`${__API_URL__}/api/ecrf/get_new_ecrf/?surveyId=${id}`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setSurveyJson(res.data.data.surveyJson);
        setCurrentPage({ trial_type: res.data.data.trial_type });
        setName(res.data.data.name || "");
        // Match returned roles (by display name) back to the static list
        const loadedRoles = (res.data.data.roles || [])
          .map((r) => SURVEY_ROLES.find((opt) => opt.label === r.name))
          .filter(Boolean);
        setSelectedRoles(loadedRoles);
      })
      .catch((err) => {
        toast.error("Something Wrong!");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getSurvey();
  }, []);

  const GetTrialTypes = () => {
    axios
      .get(`${__API_URL__}/api/ecrf/form_creator_trialtypes/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllTrialType(res.data);
      })
      .catch((err) => {});
  };

  useEffect(() => {
    GetTrialTypes();
  }, []);

  return (
    <>
      <Box sx={{ maxWidth: "95%", mx: "auto" }}>
        <NavBar active="form_creater" />

        <Stack spacing={2} my={2}>
          <Typography variant="h5" fontWeight={600}>
            {surveyId ? "Edit Source Document" : "Create Source Document"}
          </Typography>

          <Stack direction="row" gap={2}>
            <TextField
              label="Survey Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ width: "30%" }}
              size="small"
              disabled={isLoading}
            />

            <Autocomplete
              multiple
              sx={{ width: "30%" }}
              size="small"
              disabled={isLoading}
              value={currentPage.trial_type || []}
              options={allTrialType}
              getOptionLabel={(opt) => opt.label || ""}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              getOptionDisabled={(opt) =>
                (currentPage.trial_type || []).some((v) => v.value === opt.value && v.isFixed)
              }
              onChange={(_, newValue) => {
                const fixedValues = (currentPage.trial_type || []).filter((v) => v.isFixed);
                const nonFixed = newValue.filter((v) => !v.isFixed);
                setCurrentPage((prev) => ({
                  ...prev,
                  trial_type: [...fixedValues, ...nonFixed],
                }));
              }}
              renderInput={(params) => <TextField {...params} label="Trial Types" />}
            />

            <Autocomplete
              multiple
              sx={{ width: "30%" }}
              size="small"
              disabled={isLoading}
              value={selectedRoles}
              options={SURVEY_ROLES}
              getOptionLabel={(opt) => opt.label || ""}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              onChange={(_, newValue) => setSelectedRoles(newValue)}
              renderInput={(params) => <TextField {...params} label="Roles" />}
            />

            <Button variant="contained" onClick={handleSave} disabled={isLoading}>
              Save
            </Button>
          </Stack>

          {queryParameters.get("trial_id") && queryParameters.get("trial_id") !== "undefined" && (
            <Stack direction="row">
              <Button variant="contained">Finished creating eCRF blocks</Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {isLoading ? (
        <Loader />
      ) : (
        creator && (
          <Box sx={{ height: "calc(100vh - 220px)" }}>
            <SurveyCreatorComponent creator={creator} />
          </Box>
        )
      )}
    </>
  );
}
